import { memo } from "react"
import { ComposableMap, Geographies, Geography } from "react-simple-maps"
import { FaGlobe } from "react-icons/fa"

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json"

export default memo(function CountryMap({ data }) {
const dataMap = Object.fromEntries(
  data.map((d) => [d.name, d.clicks])
)

  const maxClicks = Math.max(...data.map((d) => d.clicks), 1)

  const getColor = (value) => {
    if (!value) return "#f1f5f9"
    const intensity = value / maxClicks
    if (intensity > 0.75) return "#1e40af"
    if (intensity > 0.5) return "#3b82f6"
    if (intensity > 0.25) return "#60a5fa"
    return "#bfdbfe"
  }

  const totalClicks = data.reduce((sum, d) => sum + d.clicks, 0)
  const topCountries = [...data].sort((a, b) => b.clicks - a.clicks).slice(0, 5)

  const styles = {
    card: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      border: "1px solid #e2e8f0",
      overflow: "hidden",
    },
    header: {
      padding: "20px 24px",
      borderBottom: "1px solid #e2e8f0",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    iconContainer: {
      width: "40px",
      height: "40px",
      borderRadius: "10px",
      backgroundColor: "#eff6ff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#3b82f6",
      fontSize: "16px",
    },
    title: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#1e293b",
      margin: 0,
    },
    subtitle: {
      fontSize: "13px",
      color: "#64748b",
      marginTop: "2px",
    },
    totalBadge: {
      padding: "6px 12px",
      borderRadius: "8px",
      backgroundColor: "#f1f5f9",
      fontSize: "13px",
      fontWeight: "600",
      color: "#475569",
    },
    body: {
      padding: "20px 24px",
    },
    mapContainer: {
      backgroundColor: "#f8fafc",
      borderRadius: "10px",
      padding: "16px",
      border: "1px solid #e2e8f0",
    },
    legend: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "16px",
      marginTop: "16px",
      paddingTop: "16px",
      borderTop: "1px solid #e2e8f0",
    },
    legendItem: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "12px",
      color: "#64748b",
    },
    legendDot: {
      width: "12px",
      height: "12px",
      borderRadius: "3px",
    },
    topCountries: {
      marginTop: "20px",
    },
    topCountriesTitle: {
      fontSize: "13px",
      fontWeight: "600",
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      marginBottom: "12px",
    },
    countryList: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    countryItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 12px",
      backgroundColor: "#f8fafc",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
    },
    countryName: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#1e293b",
    },
    countryClicks: {
      fontSize: "13px",
      fontWeight: "600",
      color: "#3b82f6",
    },
    progressBar: {
      flex: 1,
      height: "6px",
      backgroundColor: "#e2e8f0",
      borderRadius: "3px",
      margin: "0 12px",
      overflow: "hidden",
    },
    progressFill: {
      height: "100%",
      backgroundColor: "#3b82f6",
      borderRadius: "3px",
      transition: "width 0.3s ease",
    },
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.iconContainer}>
            <FaGlobe />
          </div>
          <div>
            <h5 style={styles.title}>Traffic by Country</h5>
            <div style={styles.subtitle}>
              {data.filter(d => d.clicks > 0).length} countries detected
            </div>
          </div>
        </div>
        <div style={styles.totalBadge}>{totalClicks.toLocaleString()} total clicks</div>
      </div>

      <div style={styles.body}>
        <div style={styles.mapContainer}>
          <ComposableMap projectionConfig={{ scale: 140 }} style={{ width: "100%", height: "auto" }}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const geoName = geo.properties.name
                  const clicks = dataMap[geoName] || 0

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getColor(clicks)}
                      stroke="#cbd5e1"
                      strokeWidth={0.5}
                      style={{
                        default: { outline: "none" },
                        hover: {
                          fill: "#2563eb",
                          outline: "none",
                          cursor: "pointer",
                        },
                        pressed: { outline: "none" },
                      }}
                    >
                      <title>
                        {geoName}: {clicks.toLocaleString()} click{clicks !== 1 ? "s" : ""}
                      </title>
                    </Geography>
                  )
                })
              }
            </Geographies>
          </ComposableMap>
        </div>

        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: "#f1f5f9" }} />
            <span>No data</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: "#bfdbfe" }} />
            <span>Low</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: "#60a5fa" }} />
            <span>Medium</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: "#3b82f6" }} />
            <span>High</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, backgroundColor: "#1e40af" }} />
            <span>Very High</span>
          </div>
        </div>

        {topCountries.length > 0 && (
          <div style={styles.topCountries}>
            <div style={styles.topCountriesTitle}>Top Countries</div>
            <div style={styles.countryList}>
              {topCountries.map((country, index) => {
                // const code = country.name.split("-")[0].toUpperCase()
                const name = country.name
                const percentage = (country.clicks / totalClicks) * 100
                return (
                  <div key={index} style={styles.countryItem}>
                    <span style={styles.countryName}>{name}</span>
                    <div style={styles.progressBar}>
                      <div
                        style={{
                          ...styles.progressFill,
                          width: `${percentage}%`,
                        }}
                      />
                    </div>
                    <span style={styles.countryClicks}>{country.clicks.toLocaleString()}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
})
